// src/pages/POS.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// 🛒 PUNTO DE VENTA - SISTEMA POS eFIT
// Con soporte para pagos mixtos, validación de permisos y UI mejorada
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "../services/api";
import Swal from "sweetalert2";
import { generarHTMLRecibo, abrirVentanaImpresion } from "../utils/recibo.utils";

// ─── Teclado Virtual Touch ───────────────────────────────────────────────────
const FILAS_TECLADO = [
  ['1','2','3','4','5','6','7','8','9','0','.','⌫'],
  ['Q','W','E','R','T','Y','U','I','O','P'],
  ['A','S','D','F','G','H','J','K','L','Ñ'],
  ['Z','X','C','V','B','N','M','⎵','↵'],
];

const TecladoVirtual = ({ inputRef, visible, onCerrar }) => {
  const presionarTecla = useCallback((tecla) => {
    const el = inputRef?.current;
    if (!el) return;
    el.focus();
    const start = el.selectionStart ?? el.value.length;
    const end   = el.selectionEnd   ?? el.value.length;
    const val   = el.value;
    let nuevoVal = val;
    let nuevaPos = start;

    if (tecla === '⌫') {
      if (start === end && start > 0) {
        nuevoVal = val.slice(0, start - 1) + val.slice(end);
        nuevaPos = start - 1;
      } else if (start !== end) {
        nuevoVal = val.slice(0, start) + val.slice(end);
        nuevaPos = start;
      }
    } else if (tecla === '⎵') {
      nuevoVal = val.slice(0, start) + ' ' + val.slice(end);
      nuevaPos = start + 1;
    } else if (tecla === '↵') {
      el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
      return;
    } else {
      nuevoVal = val.slice(0, start) + tecla + val.slice(end);
      nuevaPos = start + tecla.length;
    }

    const nativeInput = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value');
    nativeInput.set.call(el, nuevoVal);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    requestAnimationFrame(() => el.setSelectionRange(nuevaPos, nuevaPos));
  }, [inputRef]);

  if (!visible) return null;

  const st = {
    panel: {
      position: 'fixed',
      bottom: '16px',
      left: '16px',
      zIndex: 9000,
      width: '380px',
      background: 'white',
      borderRadius: '16px',
      boxShadow: '0 8px 32px rgba(59,130,246,0.18), 0 2px 8px rgba(0,0,0,0.10)',
      border: '1.5px solid #bfdbfe',
      padding: '10px 10px 12px',
    },
    header: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      marginBottom: '8px', paddingBottom: '6px',
      borderBottom: '1px solid #e2e8f0',
    },
    titulo: {
      color: '#3b82f6', fontSize: '11px', fontWeight: '700',
      letterSpacing: '1px', display: 'flex', alignItems: 'center', gap: '5px',
    },
    btnCerrar: {
      background: '#fee2e2', color: '#dc2626', border: 'none',
      borderRadius: '6px', padding: '3px 10px', cursor: 'pointer',
      fontWeight: '700', fontSize: '12px',
    },
    fila: {
      display: 'flex', justifyContent: 'center',
      gap: '4px', marginBottom: '4px',
    },
    tecla: {
      minWidth: '28px', height: '34px', borderRadius: '7px',
      background: '#f8fafc', color: '#1e293b',
      border: '1.5px solid #e2e8f0',
      fontSize: '13px', fontWeight: '600', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      userSelect: 'none', padding: '0 5px', flex: '1',
      WebkitTapHighlightColor: 'transparent',
      transition: 'background 0.08s',
    },
    teclaNum: {
      background: '#eff6ff', color: '#1e40af',
      border: '1.5px solid #bfdbfe',
    },
    teclaBorrar: {
      background: '#fee2e2', color: '#dc2626',
      border: '1.5px solid #fca5a5', flex: '1.4',
    },
    teclaEspacio: {
      flex: '3', background: '#f1f5f9',
      border: '1.5px solid #cbd5e1', color: '#64748b',
    },
    teclaEnter: {
      flex: '1.6', background: '#3b82f6', color: 'white',
      border: '1.5px solid #2563eb', fontWeight: '700',
    },
  };

  return (
    <div style={st.panel} onMouseDown={e => e.preventDefault()}>
      <div style={st.header}>
        <span style={st.titulo}>⌨️ Teclado Virtual</span>
        <button style={st.btnCerrar} onPointerDown={e => { e.preventDefault(); onCerrar(); }}>✕</button>
      </div>
      {FILAS_TECLADO.map((fila, i) => (
        <div key={i} style={st.fila}>
          {fila.map(tecla => {
            let extra = {};
            if (i === 0 && tecla !== '⌫') extra = st.teclaNum;
            if (tecla === '⌫')  extra = st.teclaBorrar;
            if (tecla === '⎵')  extra = st.teclaEspacio;
            if (tecla === '↵')  extra = st.teclaEnter;
            return (
              <button
                key={tecla}
                style={{ ...st.tecla, ...extra }}
                onPointerDown={e => { e.preventDefault(); presionarTecla(tecla); }}
              >
                {tecla}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const POS = () => {
  // Estados principales
  const [productos, setProductos] = useState([]);
  const [carrito, setCarrito] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [cliente, setCliente] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [buscandoProducto, setBuscandoProducto] = useState(false);
  const [topProductos, setTopProductos] = useState([]);
  const [cajaActiva, setCajaActiva] = useState(null);
  const [requiereNcf, setRequiereNcf] = useState(false);
  const [tipoNcf, setTipoNcf] = useState("02");
   const [busquedaCliente, setBusquedaCliente] = useState("");
const [clientesEncontrados, setClientesEncontrados] = useState([]);
const [buscandoCliente, setBuscandoCliente] = useState(false);
const [rentasPendientes, setRentasPendientes] = useState([]);
const [mostrarModalRentas, setMostrarModalRentas] = useState(false);
  
  // Estados de pago
  const [metodoPago, setMetodoPago] = useState("efectivo");
  const [montoRecibido, setMontoRecibido] = useState("");
  
  // Estados para pago mixto
  const [pagoEfectivo, setPagoEfectivo] = useState("");
  const [pagoTarjeta, setPagoTarjeta] = useState("");
  const [pagoTransferencia, setPagoTransferencia] = useState("");
  const [transferenciaReferencia, setTransferenciaReferencia] = useState("");
  const [transferenciaBanco, setTransferenciaBanco] = useState("");

  // Pagos con tarjeta — flujo progresivo parcial
  // pagosEnCurso: cobros ya confirmados [ { tipo, ultimos4, aprobacion, monto } ]
  // cobroParcial: datos del cobro que se está ingresando ahora
  const [pagosEnCurso, setPagosEnCurso] = useState([]);
  const [cobroParcial, setCobroParcial] = useState({ tipo: "", ultimos4: "", aprobacion: "", monto: "" });
  
  // Estados de permisos
  const [permisos, setPermisos] = useState({
    puede_vender: true,
    puede_descuento: false,
    puede_anular: false,
    rol: ''
  });

  const [mostrarTeclado, setMostrarTeclado] = useState(false);
  const inputBusquedaRef = useRef(null);

  const usuario = JSON.parse(localStorage.getItem("usuario"));

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔄 CARGAR DATOS INICIALES
  // ═══════════════════════════════════════════════════════════════════════════
useEffect(() => {
  cargarConfiguracion();
  cargarTopProductos();
  verificarCaja();
  cargarPermisos();
  
 // 🟢 Listener para lector de código de barras
  const handleScanner = (e) => {
    if (e.key === "Enter" && busqueda.trim() !== "") {
      buscarProductos(busqueda.trim());
    }
  };

  window.addEventListener("keydown", handleScanner);

  return () => {
    window.removeEventListener("keydown", handleScanner);
  };
}, [busqueda]);


  const cargarConfiguracion = async () => {
    try {
      const res = await api.get("/configuracion");
      setConfig(res.data);
    } catch (error) {
      console.error("Error al cargar configuración:", error);
    }
  };

  const cargarRentasParaCobro = async () => {
  try {
    const res = await api.get("/rencar/rentas/para-cobro"); // ✅ Endpoint correcto
    if (res.data.ok) {
      setRentasPendientes(res.data.rentas);
      setMostrarModalRentas(true);
    }
  } catch (error) {
    console.error("❌ Error al cargar rentas:", error);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No se pudieron obtener las rentas pendientes",
      confirmButtonColor: "#3b82f6"
    });
  }
};


const importarRentaAlCarrito = (renta) => {
  // 🛡️ Verificar que no haya otra renta en el carrito
  const rentaExistente = carrito.find(item => 
    item.tipo_item === 'servicio' && item.servicio_origen === 'renta'
  );
  
  if (rentaExistente) {
    Swal.fire({
      icon: 'warning',
      title: 'Ya hay una renta en el carrito',
      text: 'Solo puedes cobrar una renta a la vez. Elimina la actual primero.',
      confirmButtonColor: "#3b82f6"
    });
    return;
  }

  // Las rentas se pactan en USD pero se cobran en DOP.
  // Usamos total_pagar_dop si está disponible; si no, advertimos.
  const precioDOP = parseFloat(renta.total_pagar_dop || 0);
  const precioUSD = parseFloat(renta.total_pagar     || 0);
  const tasa      = parseFloat(renta.tasa_cambio     || 0);

  if (!precioDOP || precioDOP <= 0) {
    Swal.fire({
      icon: 'warning',
      title: 'Sin tasa de cambio',
      html: `Esta renta (USD ${precioUSD.toFixed(2)}) no tiene tasa USD→DOP registrada.<br>
             <strong>Registra la tasa en SuperAdmin → Billing → Tasas de cambio</strong> antes de cobrar.`,
      confirmButtonColor: "#f59e0b"
    });
    return;
  }

  // ✅ Precio en DOP para el POS (precio_usd se usa solo para display)
  const itemRenta = {
    id:              `REN-${renta.id}`,
    material_id:     null,
    tipo_item:       'servicio',
    servicio_origen: 'renta',
    servicio_id:     renta.id,
    nombre:          `RENTA: ${renta.Vehiculo.marca} ${renta.Vehiculo.modelo} (${renta.Vehiculo.placa})`,
    codigo:          `REN-${renta.id}`,
    precio_venta:    precioDOP,   // ← DOP para el POS
    precio:          precioDOP,
    precio_usd:      precioUSD,   // ← solo display en carrito
    tasa_cambio:     tasa,        // ← solo display en carrito
    stock_actual:    1,
    stock:           1,
    cantidad:        1,
    descuento:       0,
    aplica_itbis:    false
  };

  agregarAlCarrito(itemRenta);

  if (renta.Cliente) setCliente(renta.Cliente);
  setMostrarModalRentas(false);

  const fmtUSD = precioUSD.toLocaleString('en-US', { minimumFractionDigits: 2 });
  const fmtDOP = precioDOP.toLocaleString('es-DO', { minimumFractionDigits: 2 });

  Swal.fire({
    icon: 'success',
    title: '✅ Renta importada',
    html: `
      <div style="text-align: left; padding: 10px;">
        <p><strong>Vehículo:</strong> ${renta.Vehiculo.marca} ${renta.Vehiculo.modelo}</p>
        <p><strong>Placa:</strong> ${renta.Vehiculo.placa}</p>
        <p><strong>Cliente:</strong> ${renta.Cliente?.nombre || 'N/A'}</p>
        <p><strong>Monto contrato:</strong> USD $${fmtUSD}</p>
        <p style="font-size: 18px; color: #10b981;"><strong>A cobrar (DOP):</strong> RD$ ${fmtDOP}</p>
        <p style="font-size: 12px; color: #64748b;">Tasa aplicada: ${tasa} DOP/USD</p>
      </div>
    `,
    confirmButtonColor: "#10b981",
    timer: 4000
  });
};



  const cargarTopProductos = async () => {
    try {
      const res = await api.get("/ventas/top-productos?limit=10");
      setTopProductos(res.data);
    } catch (error) {
      console.error("Error al cargar top productos:", error);
    }
  };

  const verificarCaja = async () => {
    try {
      const res = await api.get("/cajas/activa");
      setCajaActiva(res.data.caja);
    } catch (error) {
      if (error.response?.status === 404) {
        setCajaActiva(null);
      }
    }
  };

  const cargarPermisos = async () => {
    try {
      const res = await api.get("/ventas/permisos");
      setPermisos(res.data);
    } catch (error) {
      console.error("Error al cargar permisos:", error);
      // Permisos por defecto basados en rol local
      const rol = usuario?.rol || '';
      setPermisos({
        puede_vender: ['superadmin', 'admin', 'supervisor', 'cajero'].includes(rol),
        puede_descuento: ['superadmin', 'admin', 'supervisor'].includes(rol),
        puede_anular: ['superadmin', 'admin', 'supervisor'].includes(rol),
        rol: rol
      });
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔍 BUSCAR PRODUCTOS Y SERVICIOS (búsqueda unificada POS)
  // ═══════════════════════════════════════════════════════════════════════════
  const buscarProductos = async (query) => {
    if (!query || query.length < 1) {
      setProductos([]);
      return;
    }

    try {
      setBuscandoProducto(true);
      const res = await api.get(`/ventas/buscar-pos?q=${encodeURIComponent(query)}`);
      setProductos(res.data.resultados || []);
    } catch (error) {
      console.error("Error al buscar en POS:", error);
      setProductos([]);
    } finally {
      setBuscandoProducto(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      buscarProductos(busqueda);
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [busqueda]);

  // Buscar clientes

const buscarClientes = async (query) => {
  if (!query || query.length < 2) {
    setClientesEncontrados([]);
    return;
  }

  try {
    setBuscandoCliente(true);
    const res = await api.get(`/clientes?buscar=${query}`);
    setClientesEncontrados(res.data.slice(0, 5)); // Máximo 5 resultados
  } catch (error) {
    console.error("Error al buscar clientes:", error);
  } finally {
    setBuscandoCliente(false);
  }
};

  // ═══════════════════════════════════════════════════════════════════════════
  // 🛒 FUNCIONES DEL CARRITO
  // ═══════════════════════════════════════════════════════════════════════════
 const agregarAlCarrito = (producto) => {
  const existe = carrito.find(item => item.id === producto.id);

  // 🟡 CASO: YA EXISTE EN EL CARRITO
  if (existe) {

    // 🔒 Si es renta, no permitir duplicar
    if (existe.tipo_item === 'servicio' && existe.servicio_origen === 'renta') {
      Swal.fire("Info", "Esta renta ya está en el carrito", "info");
      return;
    }

    // 📦 Validar stock SOLO para productos
    if (producto.tipo_item !== 'servicio') {
      if (existe.cantidad >= producto.stock_actual) {
        Swal.fire({
          icon: "warning",
          title: "Stock insuficiente",
          text: `Solo hay ${producto.stock_actual} unidades disponibles`,
          confirmButtonColor: "#3b82f6"
        });
        return;
      }
    }

    setCarrito(carrito.map(item =>
      item.id === producto.id
        ? { ...item, cantidad: item.cantidad + 1 }
        : item
    ));

  } 
  // 🟢 CASO: NO EXISTE
  else {
   setCarrito([...carrito, {
  id: producto.id,
  material_id: producto.tipo_item === 'servicio' ? null : (producto.material_id || producto.id),
  codigo: producto.codigo,
  nombre: producto.nombre,
  descripcion: producto.descripcion || null,
  precio: parseFloat(producto.precio_venta || producto.precio || 0),
  cantidad: 1,
  stock: producto.stock_actual || producto.stock || 1,
  descuento: 0,
  tipo_item: producto.tipo_item || 'producto',
  servicio_origen: producto.servicio_origen || null,
  servicio_id: producto.servicio_id || producto._partida_id || null,
  aplica_itbis: producto.aplica_itbis !== undefined ? producto.aplica_itbis : true,
  categoria: producto.categoria || null
}]);
  }

  setBusqueda("");
  setProductos([]);
};


  const actualizarCantidad = (id, nuevaCantidad) => {
    const item = carrito.find(i => i.id === id);
    
    if (nuevaCantidad > item.stock) {
      Swal.fire({
        icon: "warning",
        title: "Stock insuficiente",
        text: `Solo hay ${item.stock} unidades disponibles`,
        confirmButtonColor: "#3b82f6"
      });
      return;
    }

    if (nuevaCantidad <= 0) {
      eliminarDelCarrito(id);
      return;
    }

    setCarrito(carrito.map(item =>
      item.id === id ? { ...item, cantidad: nuevaCantidad } : item
    ));
  };

  const actualizarDescuento = (id, descuento) => {
    // Validar permisos
    if (!permisos.puede_descuento) {
      Swal.fire({
        icon: "warning",
        title: "Sin permisos",
        text: "No tienes permisos para aplicar descuentos. Contacta al supervisor.",
        confirmButtonColor: "#3b82f6"
      });
      return;
    }

    const descuentoNum = parseFloat(descuento) || 0;
    
    if (config?.pos_permite_descuentos) {
      const maxDescuento = parseFloat(config.pos_descuento_maximo) || 100;
      if (descuentoNum > maxDescuento) {
        Swal.fire({
          icon: "warning",
          title: "Descuento no permitido",
          text: `El descuento máximo es ${maxDescuento}%`,
          confirmButtonColor: "#3b82f6"
        });
        return;
      }
    }

    setCarrito(carrito.map(item =>
      item.id === id ? { ...item, descuento: descuentoNum } : item
    ));
  };

  const eliminarDelCarrito = (id) => {
    setCarrito(carrito.filter(item => item.id !== id));
  };

  const limpiarCarrito = () => {
    setCarrito([]);
    setMontoRecibido("");
    setPagoEfectivo("");
    setPagoTarjeta("");
    setPagoTransferencia("");
    setTransferenciaReferencia("");
    setTransferenciaBanco("");
    setPagosEnCurso([]);
    setCobroParcial({ tipo: "", ultimos4: "", aprobacion: "", monto: "" });
    setMetodoPago("efectivo");
     setRequiereNcf(false);
     setTipoNcf("02");
     setCliente(null);
setBusquedaCliente("");
setClientesEncontrados([]);

  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 💰 CÁLCULOS DE TOTALES
  // ═══════════════════════════════════════════════════════════════════════════
  const calcularTotales = () => {
    let subtotal = 0;
    let totalDescuentos = 0;

    carrito.forEach(item => {
      const subtotalItem = item.precio * item.cantidad;
      const descuentoItem = subtotalItem * (item.descuento / 100);
      totalDescuentos += descuentoItem;
      subtotal += subtotalItem - descuentoItem;
    });

    const itbis = config?.pos_itbis_activo
      ? subtotal * (parseFloat(config.pos_itbis_porcentaje) / 100)
      : 0;

    const total = subtotal + itbis;

    return { subtotal, itbis, total, totalDescuentos };
  };

  const { subtotal, itbis, total, totalDescuentos } = calcularTotales();

  // ═══════════════════════════════════════════════════════════════════════════
  // 💳 CÁLCULOS DE PAGO MIXTO
  // ═══════════════════════════════════════════════════════════════════════════
  const calcularPagoMixto = () => {
    const efectivo = parseFloat(pagoEfectivo) || 0;
    const tarjeta = parseFloat(pagoTarjeta) || 0;
    const transferencia = parseFloat(pagoTransferencia) || 0;
    const totalPagado = efectivo + tarjeta + transferencia;
    const restante = total - totalPagado;
    const cambio = efectivo > 0 && totalPagado > total ? totalPagado - total : 0;
    
    return { efectivo, tarjeta, transferencia, totalPagado, restante, cambio };
  };

  const pagoMixto = calcularPagoMixto();

  // Cambio para efectivo simple
  const cambioEfectivo = metodoPago === "efectivo" && montoRecibido
    ? Math.max(0, parseFloat(montoRecibido) - total)
    : 0;


  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ PROCESAR VENTA
  // ═══════════════════════════════════════════════════════════════════════════
  const procesarVenta = async () => {
    // Validaciones
    if (!cajaActiva) {
      Swal.fire({
        icon: "warning",
        title: "Sin caja abierta",
        text: "Debes abrir una caja antes de realizar ventas",
        confirmButtonColor: "#3b82f6",
        confirmButtonText: "Ir a Gestión de Cajas"
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = "/gestion-cajas";
        }
      });
      return;
    }

    if (carrito.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Carrito vacío",
        text: "Agrega productos al carrito",
        confirmButtonColor: "#3b82f6"
      });
      return;
    }

    // Validar pago según método
    if (metodoPago === "efectivo") {
      const monto = parseFloat(montoRecibido);
      if (!monto || monto < total) {
        Swal.fire({
          icon: "warning",
          title: "Monto insuficiente",
          text: `El monto recibido debe ser mayor o igual a RD$${total.toFixed(2)}`,
          confirmButtonColor: "#3b82f6"
        });
        return;
      }
    }

    if (metodoPago === "mixto") {
      if (pagoMixto.totalPagado < total) {
        Swal.fire({
          icon: "warning",
          title: "Monto insuficiente",
          text: `Faltan RD$${pagoMixto.restante.toFixed(2)} por cubrir`,
          confirmButtonColor: "#3b82f6"
        });
        return;
      }
    }

    // Validar tarjeta(s) — flujo progresivo
    if (metodoPago === "tarjeta") {
      const totalCobrado = pagosEnCurso.reduce((s, p) => s + p.monto, 0);
      if (pagosEnCurso.length === 0) {
        Swal.fire({ icon: "warning", title: "Sin cobros registrados",
          text: "Registre al menos un cobro con tarjeta antes de completar la venta.",
          confirmButtonColor: "#3b82f6" });
        return;
      }
      if (Math.abs(totalCobrado - total) > 0.01) {
        Swal.fire({ icon: "warning", title: "Monto incompleto",
          text: `Los cobros registrados (RD$${totalCobrado.toFixed(2)}) no cubren el total (RD$${total.toFixed(2)}).`,
          confirmButtonColor: "#3b82f6" });
        return;
      }
    }

    // Validar referencia de transferencia
    const hayTransferencia =
      metodoPago === "transferencia" ||
      (metodoPago === "mixto" && parseFloat(pagoTransferencia) > 0);
    if (hayTransferencia && !transferenciaReferencia.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Referencia requerida",
        text: "Ingrese el número de referencia de la transferencia confirmado por el supervisor.",
        confirmButtonColor: "#3b82f6"
      });
      return;
    }

    try {
      setLoading(true);

        // Generar NCF solo si lo requiere
  let ncfGenerado = null;
  if (requiereNcf) {
    try {
      const ncfRes = await api.post("/ncf/generar", { tipo_ncf: tipoNcf });
      ncfGenerado = ncfRes.data.ncf;
    } catch (ncfError) {
      console.error("Error al generar NCF:", ncfError);
      await Swal.fire({
        icon: "error",
        title: "Error NCF",
        text: ncfError.response?.data?.error || "No se pudo generar el comprobante fiscal"
      });
      setLoading(false);
      return;
    }
  }

      // Preparar productos
      const productosVenta = carrito.map(item => ({
  material_id: item.material_id,
  tipo_item: item.tipo_item || 'producto',
  servicio_origen: item.servicio_origen || null,
  servicio_id: item.servicio_id || null,
  descripcion: item.descripcion || null,
  nombre: item.nombre,
  codigo: item.codigo,
  cantidad: item.cantidad,
  precio_unitario: item.precio,
  descuento_linea: (item.precio * item.cantidad * item.descuento) / 100,
  aplica_itbis: item.aplica_itbis !== false
}));

      // Preparar datos de venta
      const ventaData = {
        cliente_id: cliente?.id || null,
        productos: productosVenta,
        metodo_pago: metodoPago,
        monto_recibido: metodoPago === "efectivo" 
        ? parseFloat(montoRecibido) 
        : metodoPago === "mixto" 
        ? pagoMixto.totalPagado 
        : total,
        observaciones: "",
       ncf: ncfGenerado || null,
    tipo_ncf: requiereNcf ? tipoNcf : null,
    ncf_afectado: null,
    caja_id: cajaActiva?.id || null,
     usuario_id: usuario?.id,
    usuario_nombre: usuario?.nombre,
    transferencia_referencia: hayTransferencia ? transferenciaReferencia.trim() : null,
    transferencia_banco: hayTransferencia ? (transferenciaBanco.trim() || null) : null,
    // Tarjeta — si es un solo cobro guardar en columnas dedicadas; si son varios en tarjetas[]
    tarjeta_tipo:       metodoPago === "tarjeta" && pagosEnCurso.length === 1 ? (pagosEnCurso[0].tipo || null) : null,
    tarjeta_ultimos4:   metodoPago === "tarjeta" && pagosEnCurso.length === 1 ? (pagosEnCurso[0].ultimos4 || null) : null,
    tarjeta_aprobacion: metodoPago === "tarjeta" && pagosEnCurso.length === 1 ? pagosEnCurso[0].aprobacion : null,
    tarjetas:           metodoPago === "tarjeta" && pagosEnCurso.length > 1  ? pagosEnCurso : undefined,

      };

      // Agregar detalle_pago para pago mixto
      if (metodoPago === "mixto") {
        ventaData.detalle_pago = {};
        if (pagoMixto.efectivo > 0) ventaData.detalle_pago.efectivo = pagoMixto.efectivo;
        if (pagoMixto.tarjeta > 0) ventaData.detalle_pago.tarjeta = pagoMixto.tarjeta;
        if (pagoMixto.transferencia > 0) ventaData.detalle_pago.transferencia = pagoMixto.transferencia;
      }

      const res = await api.post("/ventas", ventaData);

      // Capturar snapshot del carrito ANTES de limpiarlo, ya que limpiarCarrito()
      // corre de forma síncrona y vaciará el estado antes de que el usuario
      // haga clic en "Imprimir Recibo" en el modal de SweetAlert2.
      const carritoSnapshot = [...carrito];

      // Mostrar éxito
    Swal.fire({
  icon: "success",
  title: "¡Venta completada!",
  html: `
    <div style="text-align: left; padding: 10px;">
      <p style="margin: 8px 0;"><strong>📋 Venta:</strong> ${res.data.venta.numero_venta}</p>
      ${ncfGenerado ? `<p style="margin: 8px 0;"><strong>🧾 NCF:</strong> ${ncfGenerado}</p>` : ""}
      <p style="margin: 8px 0; font-size: 20px; color: #10b981;"><strong>💰 Total:</strong> RD$${total.toFixed(2)}</p>
      ${metodoPago === "efectivo" ? `<p style="margin: 8px 0;"><strong>💵 Cambio:</strong> RD$${cambioEfectivo.toFixed(2)}</p>` : ""}
      ${metodoPago === "mixto" && pagoMixto.cambio > 0 ? `<p style="margin: 8px 0;"><strong>💵 Cambio:</strong> RD$${pagoMixto.cambio.toFixed(2)}</p>` : ""}
    </div>
  `,
        confirmButtonColor: "#10b981",
        confirmButtonText: "🖨️ Imprimir Recibo",
        showCancelButton: true,
        cancelButtonText: "Cerrar",
        cancelButtonColor: "#64748b"
      }).then((result) => {
        if (result.isConfirmed) {
          imprimirRecibo(res.data.venta, carritoSnapshot);
        }
      });

      // Limpiar y recargar
      limpiarCarrito();
      cargarTopProductos();

    } catch (error) {
      console.error("Error al procesar venta:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || error.response?.data?.error || "No se pudo completar la venta",
        confirmButtonColor: "#3b82f6"
      });
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 🖨️ IMPRIMIR RECIBO
  // ═══════════════════════════════════════════════════════════════════════════
  const imprimirRecibo = (venta, carritoItems = carrito) => {
    // Construir objeto venta unificado mezclando la respuesta del servidor
    // (numero_venta, ncf, tipo_ncf, descuento global, etc.) con el snapshot
    // del carrito (detalles de línea con precio, cantidad y descuento).
    const ventaParaRecibo = {
      // Todos los totales (subtotal, itbis, total, descuento, monto_recibido,
      // cambio, metodo_pago, detalle_pago) vienen del servidor, que es la
      // fuente autoritativa. El servidor garantiza:
      //   venta.total = subtotal + itbis − descuento_global
      ...venta,
      // Solo sobreescribir lo que el servidor no devuelve en el POST /ventas:
      usuario_nombre: usuario?.nombre || usuario?.email || venta.usuario_nombre || '',
      fecha:          venta.fecha || new Date(),
      // Detalles de línea construidos desde el snapshot del carrito ya que la
      // respuesta de creación no incluye el array detalles completo.
      detalles: carritoItems.map(item => {
        const bruto     = item.precio * item.cantidad;
        const descLinea = (bruto * (item.descuento || 0)) / 100;
        return {
          material_nombre: item.nombre,
          cantidad:        item.cantidad,
          precio_unitario: item.precio,
          descuento_linea: descLinea,
          total_linea:     bruto - descLinea,
        };
      }),
    };

    const html = generarHTMLRecibo(ventaParaRecibo, config);
    abrirVentanaImpresion(html);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎨 RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div style={styles.container}>
      {/* Título de Página */}
      <div style={styles.pageHeader}>
        <div style={styles.pageTitle}>
          <span style={styles.pageTitleIcon}>🛒</span>
          <div>
            <h1 style={styles.pageTitleText}>Punto de Venta</h1>
            <p style={styles.pageTitleSub}>
              {usuario?.nombre} • {new Date().toLocaleDateString("es-DO", { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
        </div>
        
        <div style={styles.pageStats}>
          {cajaActiva ? (
            <div style={styles.cajaActivaBadge}>
              <span style={styles.cajaPulse}></span>
              🟢 Caja: {cajaActiva.numero_caja || cajaActiva.codigo}
            </div>
          ) : (
            <div style={styles.cajaCerradaBadge}>
              🔴 Sin caja abierta
            </div>
          )}
          {permisos.puede_descuento && (
            <div style={styles.permisoBadge}>🏷️ Descuentos</div>
          )}
        </div>
      </div>

      <div style={styles.mainContent}>
        {/* Panel Izquierdo - Productos */}
        <div style={styles.leftPanel}>
          {/* Buscador */}
          <div style={styles.searchBox}>
            <span style={styles.searchIcon}>🔍</span>
            <input
              ref={inputBusquedaRef}
              type="text"
              placeholder="Buscar por código o nombre..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              style={styles.searchInput}
              autoFocus
            />
            {busqueda && (
              <button
                onClick={() => { setBusqueda(""); setProductos([]); }}
                style={styles.clearButton}
              >
                ✕
              </button>
            )}
            <button
              onClick={() => setMostrarTeclado(v => !v)}
              style={{
                ...styles.clearButton,
                background: mostrarTeclado ? '#2563eb' : '#e2e8f0',
                color:      mostrarTeclado ? 'white'   : '#475569',
                marginLeft: '4px',
                fontSize: '18px',
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
              title="Teclado virtual"
            >
              ⌨️
            </button>
          </div>
          {config?.modulo_rentacar && ( 
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '20px' }}>
  <button 
    onClick={cargarRentasParaCobro}
    style={styles.btnRenta}
  >
    <span style={{marginRight: '8px'}}>🚗</span> 
    Cobrar Renta de Vehículo
  </button>
</div>
)}
          {/* Resultados de búsqueda */}
          {buscandoProducto && (
            <div style={styles.loadingBox}>
              <div style={styles.spinner}></div>
              <span>Buscando...</span>
            </div>
          )}

          {productos.length > 0 && (
            <div style={styles.productList}>
              <div style={styles.productListHeader}>
                {productos.length} resultado(s)
              </div>
              {productos.map((producto) => {
                const esServicio = producto._tipo === 'servicio';
                return (
                  <div
                    key={producto.id}
                    style={{
                      ...styles.productCard,
                      borderLeft: esServicio ? '3px solid #8b5cf6' : '3px solid #3b82f6'
                    }}
                    onClick={() => agregarAlCarrito(producto)}
                  >
                    <div style={styles.productInfo}>
                      <div style={styles.productName}>
                        {esServicio ? '🔧 ' : '📦 '}{producto.nombre}
                      </div>
                      <div style={styles.productMeta}>
                        <span style={styles.productCode}>
                          <span style={styles.codeIcon}>📋</span> {producto.codigo}
                        </span>
                        {esServicio ? (
                          <span style={{ fontSize: '11px', color: '#8b5cf6' }}>
                            {producto.categoria || 'Servicio'} · {producto.unidad}
                          </span>
                        ) : (
                          <span style={{
                            ...styles.productStock,
                            color: producto.stock_actual > 10 ? '#10b981' : producto.stock_actual > 3 ? '#f59e0b' : '#ef4444'
                          }}>
                            <span style={styles.stockIcon}>📦</span> {producto.stock_actual} {producto.unidad || 'und'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={styles.productPriceBox}>
                      <div style={styles.productPrice}>
                        RD${parseFloat(producto.precio_venta || 0).toFixed(2)}
                      </div>
                      <div style={styles.addButton}>+</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!buscandoProducto && busqueda && productos.length === 0 && (
            <div style={styles.emptyBox}>
              <div style={styles.emptyIcon}>🔍</div>
              <div style={styles.emptyText}>No se encontraron productos ni servicios</div>
              <div style={styles.emptyHint}>Intenta con otro término de búsqueda</div>
            </div>
          )}
          

          {/* Top Productos */}
          {!busqueda && topProductos.length > 0 && (
            <div style={styles.topProductsSection}>
              <div style={styles.topProductsHeader}>
                <div style={styles.topProductsIcon}>🔥</div>
                <div>
                  <div style={styles.topProductsTitle}>Productos Más Vendidos</div>
                  <div style={styles.topProductsSubtitle}>Últimos 30 días</div>
                </div>
              </div>
              <div style={styles.topProductsGrid}>
         {topProductos.map((producto, index) => (
  <div
    // ✅ Agregamos el index para garantizar que nunca se repita la key
    key={`top-${producto.id}-${index}`} 
    style={styles.topProductCard}
    onClick={() => agregarAlCarrito({
      id: producto.id,
      codigo: producto.codigo,
      nombre: producto.nombre,
      precio_venta: producto.precio_venta,
      stock_actual: producto.stock_actual,
    })}
                  >
                    <div style={styles.topProductRank}>
                      <span style={styles.rankNumber}>#{index + 1}</span>
                    </div>
                    <div style={styles.topProductInfo}>
                      <div style={styles.topProductName}>{producto.nombre}</div>
                      <div style={styles.topProductStats}>
                        <span style={styles.topProductSales}>📊 {producto.total_vendido} vendidos</span>
                        <span style={styles.topProductStock}>Stock: {producto.stock_actual}</span>
                      </div>
                    </div>
                    <div style={styles.topProductPrice}>
                      RD${parseFloat(producto.precio_venta || 0).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Panel Derecho - Carrito */}
        <div style={styles.rightPanel}>
          {/* Header del carrito */}
          <div style={styles.cartHeader}>
            <div style={styles.cartHeaderIcon}>🛒</div>
            
            <div>
              <div style={styles.cartTitle}>Carrito de Compra</div>
              <div style={styles.cartSubtitle}>{carrito.length} producto(s)</div>
            </div>
          </div>
          {/* Sección Cliente */}
<div style={{padding: "16px", borderBottom: "1px solid #e2e8f0"}}>
  <label style={{display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: "700", color: "#1e293b", marginBottom: "10px"}}>
    <span>👤</span> Cliente
  </label>
  
  {cliente ? (
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px",
      background: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
      borderRadius: "10px",
      border: "1px solid #6ee7b7"
    }}>
      <div>
        <div style={{fontWeight: "700", color: "#065f46"}}>{cliente.nombre}</div>
        {cliente.rnc && <div style={{fontSize: "12px", color: "#047857"}}>RNC: {cliente.rnc}</div>}
      </div>
      <button
        onClick={() => setCliente(null)}
        style={{
          background: "#fee2e2",
          border: "none",
          borderRadius: "6px",
          padding: "6px 12px",
          cursor: "pointer",
          fontSize: "12px",
          color: "#991b1b",
          fontWeight: "600"
        }}
      >
        ✕ Quitar
      </button>
    </div>
  ) : (
    <div style={{position: "relative"}}>
      <input
        type="text"
        placeholder="Buscar por nombre, RNC o teléfono..."
        value={busquedaCliente}
        onChange={(e) => {
          setBusquedaCliente(e.target.value);
          buscarClientes(e.target.value);
        }}
        style={{
          width: "100%",
          padding: "12px",
          paddingRight: "40px",
          fontSize: "14px",
          border: "2px solid #e2e8f0",
          borderRadius: "10px",
          outline: "none",
          boxSizing: "border-box"
        }}
      />
      {buscandoCliente && (
        <div style={{position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)"}}>
          <div style={{width: "18px", height: "18px", border: "2px solid #e2e8f0", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "spin 0.8s linear infinite"}}></div>
        </div>
      )}
      
      {/* Lista de resultados */}
      {clientesEncontrados.length > 0 && (
        <div style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          background: "white",
          border: "2px solid #e2e8f0",
          borderRadius: "10px",
          marginTop: "4px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          zIndex: 100,
          maxHeight: "200px",
          overflowY: "auto"
        }}>
          {clientesEncontrados.map((c) => (
            <div
              key={c.id}
              onClick={() => {
                setCliente(c);
                setBusquedaCliente("");
                setClientesEncontrados([]);
              }}
              style={{
                padding: "12px",
                cursor: "pointer",
                borderBottom: "1px solid #f1f5f9",
                transition: "background 0.2s"
              }}
              onMouseEnter={(e) => e.target.style.background = "#f8fafc"}
              onMouseLeave={(e) => e.target.style.background = "white"}
            >
              <div style={{fontWeight: "600", color: "#1e293b"}}>{c.nombre}</div>
              <div style={{fontSize: "12px", color: "#64748b"}}>
                {c.rnc && `RNC: ${c.rnc}`}
                {c.rnc && c.telefono && " • "}
                {c.telefono && `Tel: ${c.telefono}`}
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div style={{fontSize: "11px", color: "#94a3b8", marginTop: "6px"}}>
        💡 Sin cliente = Consumidor Final
      </div>
    </div>
  )}
</div>

          {/* Lista del carrito */}
          <div style={styles.cartSection}>
            {carrito.length === 0 ? (
              <div style={styles.emptyCart}>
                <div style={styles.emptyCartIcon}>🛒</div>
                <div style={styles.emptyCartText}>Carrito vacío</div>
                <div style={styles.emptyCartHint}>Busca productos para agregar</div>
              </div>
            ) : (
              <div style={styles.cartList}>
                {carrito.map((item, index) => {
                  const esRenta = item.tipo_item === 'servicio' && item.servicio_origen === 'renta';
                  return (
                  <div key={item.id} style={esRenta ? {...styles.cartItem, ...styles.cartItemRenta} : styles.cartItem}>
                    <div style={styles.cartItemHeader}>
                      <div style={styles.cartItemIndex}>{index + 1}</div>
                      <div style={styles.cartItemName}>
                        {esRenta && (
                          <span style={styles.rentaBadge}>🚗 RENTA</span>
                        )}
                        {/* Strip the "RENTA: " prefix since the badge already shows it */}
                        {esRenta ? item.nombre.replace(/^RENTA:\s*/i, '') : item.nombre}
                      </div>
                      <button
                        onClick={() => eliminarDelCarrito(item.id)}
                        style={styles.btnDelete}
                      >
                        🗑️
                      </button>
                    </div>

                    <div style={styles.cartItemControls}>
                      <div style={styles.quantityControl}>
                        <button
                          onClick={() => actualizarCantidad(item.id, item.cantidad - 1)}
                          style={styles.btnQty}
                          disabled={esRenta}
                        >
                          −
                        </button>
                        <input
                          type="number"
                          value={item.cantidad}
                          onChange={(e) => actualizarCantidad(item.id, parseInt(e.target.value) || 1)}
                          style={styles.inputQty}
                          min="1"
                          max={item.stock}
                          readOnly={esRenta}
                        />
                        <button
                          onClick={() => actualizarCantidad(item.id, item.cantidad + 1)}
                          style={styles.btnQty}
                          disabled={esRenta}
                        >
                          +
                        </button>
                      </div>

                      <div style={styles.priceInfo}>
                        {esRenta ? (
                          <div style={{ textAlign: 'right' }}>
                            <span style={{ ...styles.priceUnit, color: '#6366f1', fontWeight: '700' }}>
                              RD$ {item.precio.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                            </span>
                            {item.precio_usd && (
                              <span style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                                USD ${parseFloat(item.precio_usd).toLocaleString('en-US', { minimumFractionDigits: 2 })} × {parseFloat(item.tasa_cambio || 0).toFixed(2)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <>
                            <span style={styles.priceUnit}>RD${item.precio.toFixed(2)} c/u</span>
                            <span style={styles.priceTotal}>
                              RD${(item.precio * item.cantidad * (1 - item.descuento / 100)).toFixed(2)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Descuento - Solo si tiene permisos y no es renta */}
                    {permisos.puede_descuento && !esRenta && (
                      <div style={styles.discountControl}>
                        <span style={styles.discountLabel}>
                          <span style={styles.discountIcon}>🏷️</span> Descuento:
                        </span>
                        <div style={styles.discountInputBox}>
                          <input
                            type="number"
                            value={item.descuento}
                            onChange={(e) => actualizarDescuento(item.id, e.target.value)}
                            style={styles.inputDiscount}
                            min="0"
                            max="100"
                          />
                          <span style={styles.discountPercent}>%</span>
                        </div>
                        {item.descuento > 0 && (
                          <span style={styles.discountSaved}>
                            -RD${((item.precio * item.cantidad * item.descuento) / 100).toFixed(2)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Totales y Pago */}
          {carrito.length > 0 && (
            <>
              {/* Totales */}
              <div style={styles.totalsSection}>
                <div style={styles.totalRow}>
                  <span>Subtotal:</span>
                  <span style={styles.totalValue}>RD${subtotal.toFixed(2)}</span>
                </div>

                {totalDescuentos > 0 && (
                  <div style={{...styles.totalRow, color: '#ef4444'}}>
                    <span>Descuentos:</span>
                    <span>-RD${totalDescuentos.toFixed(2)}</span>
                  </div>
                )}

                {config?.pos_itbis_activo && (
                  <div style={styles.totalRow}>
                    <span>ITBIS ({config.pos_itbis_porcentaje || 18}%):</span>
                    <span style={styles.totalValue}>RD${itbis.toFixed(2)}</span>
                  </div>
                )}

                <div style={styles.totalRowFinal}>
                  <span>TOTAL A PAGAR</span>
                  <span style={styles.totalFinal}>RD${total.toFixed(2)}</span>
                </div>
              </div>

              {/* Método de Pago */}
              <div style={styles.paymentSection}>
                <div style={styles.paymentLabel}>
                  <span style={styles.paymentIcon}>💳</span> Método de Pago
                </div>
                
                <div style={styles.paymentOptions}>
                  {['efectivo', 'tarjeta', 'transferencia', 'mixto'].map((metodo) => (
                    <div
                      key={metodo}
                      style={{
                        ...styles.paymentOption,
                        ...(metodoPago === metodo ? styles.paymentOptionActive : {})
                      }}
                      onClick={() => {
                        setMetodoPago(metodo);
                        // Limpiar campos al cambiar método
                        setMontoRecibido("");
                        setPagoEfectivo("");
                        setPagoTarjeta("");
                        setPagoTransferencia("");
                      }}
                    >
                      <span style={styles.paymentOptionIcon}>
                        {metodo === 'efectivo' && '💵'}
                        {metodo === 'tarjeta' && '💳'}
                        {metodo === 'transferencia' && '🏦'}
                        {metodo === 'mixto' && '🔀'}
                      </span>
                      <span style={styles.paymentOptionLabel}>
                        {metodo.charAt(0).toUpperCase() + metodo.slice(1)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Campos según método de pago */}
                {metodoPago === "efectivo" && (
                  <div style={styles.efectivoSection}>
                    <label style={styles.label}>Monto Recibido:</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={montoRecibido}
                      onChange={(e) => setMontoRecibido(e.target.value)}
                      style={styles.inputMonto}
                      placeholder="0.00"
                    />
                    {parseFloat(montoRecibido) >= total && (
                      <div style={styles.cambioBox}>
                        <span style={styles.cambioLabel}>💵 Cambio:</span>
                        <span style={styles.cambioMonto}>RD${cambioEfectivo.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Campos banco + referencia — visible para transferencia simple */}
                {metodoPago === "transferencia" && (
                  <div style={styles.efectivoSection}>
                    <label style={styles.label}>Banco emisor</label>
                    <input
                      type="text"
                      value={transferenciaBanco}
                      onChange={(e) => setTransferenciaBanco(e.target.value)}
                      style={{...styles.inputMonto, marginBottom: "10px"}}
                      placeholder="Ej: Banco Popular, BHD..."
                      maxLength={100}
                    />
                    <label style={styles.label}>
                      Número de referencia <span style={{color:"#ef4444"}}>*</span>
                    </label>
                    <input
                      type="text"
                      value={transferenciaReferencia}
                      onChange={(e) => setTransferenciaReferencia(e.target.value)}
                      style={{
                        ...styles.inputMonto,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                      placeholder="Confirmado con supervisor"
                      maxLength={100}
                    />
                    <p style={{fontSize:"11px", color:"#64748b", margin:"4px 0 0"}}>
                      Verificar con supervisor antes de ingresar
                    </p>
                  </div>
                )}

                {/* ── TARJETA — flujo progresivo parcial ── */}
                {metodoPago === "tarjeta" && (() => {
                  const totalCobrado = pagosEnCurso.reduce((s, p) => s + p.monto, 0);
                  const pendiente    = Math.max(0, total - totalCobrado);
                  const hayTerminal  = config?.terminal_tipo && config.terminal_tipo !== 'ninguno';

                  const registrarCobro = () => {
                    // Si el campo está vacío, tomar el saldo pendiente (igual que enviarAlDatafono)
                    const monto = parseFloat(cobroParcial.monto) || pendiente;
                    if (!monto || monto <= 0) {
                      Swal.fire({ icon:"warning", title:"Monto inválido", text:"Ingrese el monto a cobrar.", confirmButtonColor:"#3b82f6" });
                      return;
                    }
                    if (monto > pendiente + 0.01) {
                      Swal.fire({ icon:"warning", title:"Monto excede pendiente",
                        text:`El monto (RD$${monto.toFixed(2)}) supera lo pendiente (RD$${pendiente.toFixed(2)}). Con tarjeta no hay vuelto.`,
                        confirmButtonColor:"#3b82f6" });
                      return;
                    }
                    if (!cobroParcial.aprobacion.trim()) {
                      Swal.fire({ icon:"warning", title:"Código de aprobación requerido",
                        text:"Ingrese el código devuelto por el datafono.", confirmButtonColor:"#3b82f6" });
                      return;
                    }
                    setPagosEnCurso([...pagosEnCurso, { ...cobroParcial, monto }]);
                    setCobroParcial({ tipo:"", ultimos4:"", aprobacion:"", monto:"" });
                  };

                  const enviarAlDatafono = async () => {
                    const monto = parseFloat(cobroParcial.monto) || pendiente;
                    try {
                      const resp = await fetch(`http://localhost:7070/sale`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ monto, referencia: `POS-${Date.now()}` })
                      });
                      if (!resp.ok) throw new Error("Sin respuesta del terminal");
                      const data = await resp.json();
                      setCobroParcial(prev => ({
                        ...prev,
                        monto:      String(data.monto    || monto),
                        tipo:       data.card_type       || prev.tipo,
                        ultimos4:   data.last_4          || prev.ultimos4,
                        aprobacion: data.approval_code   || prev.aprobacion,
                      }));
                    } catch {
                      Swal.fire({ icon:"error", title:"Terminal no disponible",
                        text:"No se pudo conectar al agente local. Ingrese los datos manualmente.",
                        confirmButtonColor:"#3b82f6" });
                    }
                  };

                  return (
                    <div style={styles.efectivoSection}>
                      {/* Cobros ya registrados */}
                      {pagosEnCurso.map((p, i) => (
                        <div key={i} style={{
                          display:"flex", justifyContent:"space-between", alignItems:"center",
                          background:"#f0fdf4", border:"1px solid #86efac", borderRadius:"8px",
                          padding:"8px 12px", marginBottom:"6px", fontSize:"12px",
                        }}>
                          <span>
                            ✅ <strong>{p.tipo || "Tarjeta"}</strong>
                            {p.ultimos4 ? ` ****${p.ultimos4}` : ""} — RD${p.monto.toFixed(2)}
                            <span style={{color:"#64748b"}}> #{p.aprobacion}</span>
                          </span>
                          <button type="button" onClick={() => setPagosEnCurso(pagosEnCurso.filter((_,j)=>j!==i))}
                            style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:"16px"}}>×</button>
                        </div>
                      ))}

                      {/* Balance pendiente */}
                      <div style={{
                        textAlign:"center", padding:"8px", borderRadius:"8px", marginBottom:"10px",
                        background: pendiente < 0.01 ? "#f0fdf4" : "#fff7ed",
                        border: `1px solid ${pendiente < 0.01 ? "#86efac" : "#fed7aa"}`,
                        fontWeight:"700", fontSize:"13px",
                        color: pendiente < 0.01 ? "#16a34a" : "#ea580c",
                      }}>
                        {pendiente < 0.01
                          ? `✅ Total cubierto — RD$${totalCobrado.toFixed(2)}`
                          : `🔴 Pendiente: RD$${pendiente.toFixed(2)}`}
                      </div>

                      {/* Formulario del siguiente cobro */}
                      {pendiente > 0.01 && (
                        <div style={{background:"#f8fafc", borderRadius:"8px", padding:"12px", border:"1px solid #e2e8f0"}}>
                          <div style={{display:"flex", gap:"8px", marginBottom:"8px"}}>
                            <div style={{flex:1}}>
                              <label style={styles.label}>Monto a cobrar <span style={{color:"#ef4444"}}>*</span></label>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={cobroParcial.monto}
                                onChange={e => setCobroParcial(p=>({...p, monto:e.target.value}))}
                                style={styles.inputMonto}
                                placeholder={pendiente.toFixed(2)}
                              />
                            </div>
                          </div>
                          {hayTerminal && (
                            <button type="button" onClick={enviarAlDatafono} style={{
                              width:"100%", padding:"9px", marginBottom:"10px",
                              background:"#0ea5e9", color:"#fff", border:"none",
                              borderRadius:"6px", cursor:"pointer", fontWeight:"600", fontSize:"13px",
                            }}>
                              📡 Enviar al datafono ({config.terminal_tipo})
                            </button>
                          )}
                          <label style={styles.label}>Tipo de tarjeta</label>
                          <select value={cobroParcial.tipo}
                            onChange={e => setCobroParcial(p=>({...p, tipo:e.target.value}))}
                            style={{...styles.inputMonto, marginBottom:"8px"}}>
                            <option value="">-- Seleccione --</option>
                            {["Visa","Mastercard","Amex","Discover","Diners"].map(b=>(
                              <option key={b} value={b}>{b}</option>
                            ))}
                          </select>
                          <label style={styles.label}>Últimos 4 dígitos</label>
                          <input type="text" inputMode="numeric" maxLength={4}
                            value={cobroParcial.ultimos4}
                            onChange={e => setCobroParcial(p=>({...p, ultimos4:e.target.value.replace(/\D/g,"")}))}
                            style={{...styles.inputMonto, marginBottom:"8px", letterSpacing:"0.2em"}}
                            placeholder="****"
                          />
                          <label style={styles.label}>
                            Código de aprobación <span style={{color:"#ef4444"}}>*</span>
                          </label>
                          <input type="text" maxLength={20}
                            value={cobroParcial.aprobacion}
                            onChange={e => setCobroParcial(p=>({...p, aprobacion:e.target.value.toUpperCase()}))}
                            style={{...styles.inputMonto, marginBottom:"10px", letterSpacing:"0.05em"}}
                            placeholder="Del datafono"
                          />
                          <button type="button" onClick={registrarCobro} style={{
                            width:"100%", padding:"9px",
                            background:"#10b981", color:"#fff", border:"none",
                            borderRadius:"6px", cursor:"pointer", fontWeight:"600", fontSize:"13px",
                          }}>
                            ✓ Registrar cobro
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {metodoPago === "mixto" && (
                  <div style={styles.mixtoSection}>
                    <div style={styles.mixtoRow}>
                      <label style={styles.mixtoLabel}>💵 Efectivo:</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={pagoEfectivo}
                        onChange={(e) => setPagoEfectivo(e.target.value)}
                        style={styles.mixtoInput}
                        placeholder="0.00"
                      />
                    </div>
                    <div style={styles.mixtoRow}>
                      <label style={styles.mixtoLabel}>💳 Tarjeta:</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={pagoTarjeta}
                        onChange={(e) => setPagoTarjeta(e.target.value)}
                        style={styles.mixtoInput}
                        placeholder="0.00"
                      />
                    </div>
                    <div style={styles.mixtoRow}>
                      <label style={styles.mixtoLabel}>🏦 Transferencia:</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={pagoTransferencia}
                        onChange={(e) => setPagoTransferencia(e.target.value)}
                        style={styles.mixtoInput}
                        placeholder="0.00"
                      />
                    </div>
                    {parseFloat(pagoTransferencia) > 0 && (
                      <div style={{marginBottom: "8px"}}>
                        <label style={{...styles.mixtoLabel, width: "100%", marginBottom: "4px"}}>
                          Referencia transf. <span style={{color:"#ef4444"}}>*</span>
                        </label>
                        <input
                          type="text"
                          value={transferenciaReferencia}
                          onChange={(e) => setTransferenciaReferencia(e.target.value)}
                          style={{
                            ...styles.mixtoInput,
                            width: "100%",
                            textTransform: "uppercase",
                            fontSize: "12px",
                          }}
                          placeholder="Núm. de referencia"
                          maxLength={100}
                        />
                      </div>
                    )}

                    {/* Resumen de pago mixto */}
                    <div style={styles.mixtoResumen}>
                      <div style={styles.mixtoResumenRow}>
                        <span>Total pagado:</span>
                        <span style={{fontWeight: '700', color: pagoMixto.totalPagado >= total ? '#10b981' : '#f59e0b'}}>
                          RD${pagoMixto.totalPagado.toFixed(2)}
                        </span>
                      </div>
                      {pagoMixto.restante > 0 && (
                        <div style={{...styles.mixtoResumenRow, color: '#ef4444'}}>
                          <span>Falta:</span>
                          <span style={{fontWeight: '700'}}>RD${pagoMixto.restante.toFixed(2)}</span>
                        </div>
                      )}
                      {pagoMixto.cambio > 0 && (
                        <div style={{...styles.mixtoResumenRow, color: '#10b981'}}>
                          <span>Cambio:</span>
                          <span style={{fontWeight: '700'}}>RD${pagoMixto.cambio.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* Comprobante Fiscal */}
              <div style={{padding: "16px 20px", borderTop: "1px solid #e2e8f0"}}>
                <label style={styles.label}>🧾 Comprobante Fiscal</label>
                
                <div style={{display: "flex", gap: "12px", marginBottom: "12px"}}>
                  <button
                    type="button"
                    style={{
                      flex: 1,
                      padding: "12px",
                      border: "2px solid",
                      borderColor: !requiereNcf ? "#3b82f6" : "#e2e8f0",
                      borderRadius: "10px",
                      background: !requiereNcf ? "#eff6ff" : "white",
                      cursor: "pointer",
                      fontWeight: "600",
                      fontSize: "13px",
                      color: !requiereNcf ? "#1e40af" : "#64748b"
                    }}
                    onClick={() => setRequiereNcf(false)}
                  >
                    ❌ Sin Comprobante
                  </button>
                  <button
                    type="button"
                    style={{
                      flex: 1,
                      padding: "12px",
                      border: "2px solid",
                      borderColor: requiereNcf ? "#10b981" : "#e2e8f0",
                      borderRadius: "10px",
                      background: requiereNcf ? "#d1fae5" : "white",
                      cursor: "pointer",
                      fontWeight: "600",
                      fontSize: "13px",
                      color: requiereNcf ? "#065f46" : "#64748b"
                    }}
                    onClick={() => setRequiereNcf(true)}
                  >
                    ✅ Con Comprobante
                  </button>
                </div>

                {requiereNcf && (
                  <select
                    style={{
                      width: "100%",
                      padding: "12px",
                      fontSize: "14px",
                      border: "2px solid #10b981",
                      borderRadius: "8px",
                      background: "#f0fdf4",
                      cursor: "pointer",
                      fontWeight: "600"
                    }}
                    value={tipoNcf}
                    onChange={(e) => setTipoNcf(e.target.value)}
                  >
                    <option disabled>── Serie B ──</option>
                    <option value="02">B02 - Consumidor Final</option>
                    <option value="01">B01 - Crédito Fiscal (RNC)</option>
                    <option value="14">B14 - Régimen Especial</option>
                    <option value="15">B15 - Gubernamental</option>
                    <option disabled>── Serie E (e-CF) ──</option>
                    <option value="32">E32 - Consumidor Final Electrónico</option>
                    <option value="31">E31 - Crédito Fiscal Electrónico</option>
                    <option value="33">E33 - Nota de Débito Electrónica</option>
                    <option value="34">E34 - Nota de Crédito Electrónica</option>
                  </select>
                )}
              </div>

              {/* Botón Procesar */}
              <button
                onClick={procesarVenta}
                disabled={loading || !cajaActiva}
                style={{
                  ...styles.btnProcesar,
                  opacity: (loading || !cajaActiva) ? 0.6 : 1,
                  cursor: (loading || !cajaActiva) ? "not-allowed" : "pointer",
                }}
              >
                <div style={styles.btnContent}>
                  {loading ? (
                    <div style={styles.btnLoading}>
                      <div style={styles.btnSpinner}></div>
                      <span>Procesando...</span>
                    </div>
                  ) : (
                    <>
                      <span style={styles.btnIcon}>✓</span>
                      <span>PROCESAR VENTA</span>
                    </>
                  )}
                </div>
              </button>
            </>
          )}
        </div>
      </div>
{/* 🚗 MODAL DE RENTAS PENDIENTES */}
{mostrarModalRentas && (
  <div style={styles.modalOverlay}>
    <div style={styles.modalContent}>
      <div style={styles.modalHeader}>
        <h2 style={{margin: 0}}>🚗 Rentas Listas para Cobro</h2>
        <button onClick={() => setMostrarModalRentas(false)} style={styles.btnCloseModal}>✕</button>
      </div>
      <div style={styles.modalBody}>
        {rentasPendientes.length === 0 ? (
          <p style={{textAlign: 'center', padding: '20px'}}>No hay rentas con inspección completada.</p>
        ) : (
          rentasPendientes.map(r => (
            <div key={r.id} style={styles.rentaRow}>
              <div>
                <div style={{fontWeight: 'bold'}}>{r.Vehiculo.marca} {r.Vehiculo.modelo}</div>
                <div style={{fontSize: '12px', color: '#64748b'}}>Placa: {r.Vehiculo.placa} • Cliente: {r.Cliente?.nombre}</div>
              </div>
              <div style={{textAlign: 'right'}}>
                <div style={{fontWeight: '800', color: '#1e3a8a'}}>
                  USD ${parseFloat(r.total_pagar).toFixed(2)}
                </div>
                {r.total_pagar_dop && (
                  <div style={{fontSize: '11px', color: '#6366f1', fontWeight: '600'}}>
                    ≈ RD$ {parseFloat(r.total_pagar_dop).toLocaleString('es-DO', {minimumFractionDigits: 2})}
                  </div>
                )}
                <button
                  onClick={() => importarRentaAlCarrito(r)}
                  style={styles.btnImportar}
                >
                  📥 Cobrar
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  </div>
)}
      {/* Estilos de animación */}
      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
      `}</style>

      {/* Teclado Virtual */}
      <TecladoVirtual
        inputRef={inputBusquedaRef}
        visible={mostrarTeclado}
        onCerrar={() => setMostrarTeclado(false)}
      />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS eFIT
// ═══════════════════════════════════════════════════════════════════════════════
const styles = {
  container: { minHeight: "calc(100vh - 70px)", background: "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)", padding: "24px 32px", fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif" },
  
  // Page Header
  pageHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", flexWrap: "wrap", gap: "16px" },
  pageTitle: { display: "flex", alignItems: "center", gap: "16px" },
  pageTitleIcon: { fontSize: "36px", background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)", padding: "14px", borderRadius: "16px", border: "2px solid #93c5fd" },
  pageTitleText: { fontSize: "28px", fontWeight: "800", color: "#1e293b", margin: 0 },
  pageTitleSub: { fontSize: "14px", color: "#64748b", margin: "4px 0 0 0" },
  pageStats: { display: "flex", gap: "12px", flexWrap: "wrap" },
  cajaActivaBadge: { display: "flex", alignItems: "center", gap: "10px", padding: "12px 20px", background: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)", color: "#065f46", borderRadius: "12px", fontWeight: "700", fontSize: "14px", border: "2px solid #6ee7b7" },
  cajaPulse: { width: "8px", height: "8px", background: "#10b981", borderRadius: "50%", animation: "pulse 1.5s infinite" },
  cajaCerradaBadge: { padding: "12px 20px", background: "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)", color: "#991b1b", borderRadius: "12px", fontWeight: "700", fontSize: "14px", border: "2px solid #fca5a5" },
  permisoBadge: { padding: "12px 20px", background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)", color: "#92400e", borderRadius: "12px", fontWeight: "700", fontSize: "14px", border: "2px solid #fcd34d" },

  // Main Content
  mainContent: { display: "grid", gridTemplateColumns: "1fr 400px", gap: "24px", alignItems: "start" },

  // Left Panel
  leftPanel: { background: "white", borderRadius: "20px", padding: "24px", boxShadow: "0 4px 20px rgba(0,0,0,0.08)", border: "1px solid rgba(59, 130, 246, 0.1)" },
  searchBox: { position: "relative", marginBottom: "20px" },
  searchIcon: { position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", fontSize: "18px", opacity: "0.5" },
  searchInput: { width: "100%", padding: "16px 50px", fontSize: "16px", border: "2px solid #e2e8f0", borderRadius: "14px", outline: "none", boxSizing: "border-box", background: "#f8fafc" },
  clearButton: { position: "absolute", right: "16px", top: "50%", transform: "translateY(-50%)", background: "#e2e8f0", border: "none", borderRadius: "50%", width: "28px", height: "28px", cursor: "pointer", fontSize: "14px", color: "#64748b" },
  loadingBox: { display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", padding: "40px", color: "#64748b" },
  spinner: { width: "24px", height: "24px", border: "3px solid #e2e8f0", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  emptyBox: { textAlign: "center", padding: "40px 20px" },
  emptyIcon: { fontSize: "48px", marginBottom: "12px", opacity: "0.3" },
  emptyText: { fontSize: "16px", fontWeight: "600", color: "#475569", marginBottom: "4px" },
  emptyHint: { fontSize: "13px", color: "#94a3b8" },

  // Product List
  productList: { maxHeight: "400px", overflowY: "auto" },
  productListHeader: { padding: "10px 14px", background: "linear-gradient(90deg, #eff6ff 0%, #f8fafc 100%)", borderRadius: "8px", marginBottom: "12px", fontSize: "13px", fontWeight: "600", color: "#3b82f6" },
  productCard: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", border: "1px solid #e2e8f0", borderLeft: "4px solid #3b82f6", borderRadius: "12px", marginBottom: "10px", cursor: "pointer", transition: "all 0.2s", background: "white" },
  productInfo: { flex: 1 },
  productName: { fontSize: "15px", fontWeight: "700", color: "#1e293b", marginBottom: "6px" },
  productMeta: { display: "flex", gap: "16px", flexWrap: "wrap" },
  productCode: { display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#64748b" },
  codeIcon: { fontSize: "12px" },
  productStock: { display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: "600" },
  stockIcon: { fontSize: "12px" },
  productPriceBox: { display: "flex", alignItems: "center", gap: "12px" },
  productPrice: { fontSize: "16px", fontWeight: "800", color: "#1e3a8a" },
  addButton: { width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)", color: "white", borderRadius: "8px", fontSize: "18px", fontWeight: "700" },

  // Top Products
  topProductsSection: { marginTop: "24px", paddingTop: "24px", borderTop: "2px solid #e2e8f0" },
  topProductsHeader: { display: "flex", alignItems: "center", gap: "14px", marginBottom: "20px" },
  topProductsIcon: { fontSize: "32px", background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)", padding: "10px", borderRadius: "12px" },
  topProductsTitle: { fontSize: "16px", fontWeight: "700", color: "#1e293b", margin: 0 },
  topProductsSubtitle: { fontSize: "12px", color: "#64748b", margin: "2px 0 0 0" },
  topProductsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: "12px" },
  topProductCard: { display: "flex", alignItems: "center", gap: "12px", padding: "12px 14px", background: "white", border: "1px solid #e2e8f0", borderRadius: "12px", cursor: "pointer", transition: "all 0.2s" },
  topProductRank: { width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)", borderRadius: "8px", flexShrink: 0 },
  rankNumber: { color: "white", fontSize: "11px", fontWeight: "800" },
  topProductInfo: { flex: 1, minWidth: 0 },
  topProductName: { fontSize: "13px", fontWeight: "600", color: "#1e293b", marginBottom: "4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  topProductStats: { display: "flex", flexDirection: "column", gap: "2px" },
  topProductSales: { fontSize: "11px", color: "#3b82f6", fontWeight: "600" },
  topProductStock: { fontSize: "10px", color: "#64748b" },
  topProductPrice: { fontSize: "14px", fontWeight: "800", color: "#1e3a8a", flexShrink: 0 },

  // Right Panel - Cart
  rightPanel: { background: "white", borderRadius: "20px", boxShadow: "0 4px 20px rgba(0,0,0,0.08)", overflow: "hidden", border: "1px solid rgba(59, 130, 246, 0.1)", position: "sticky", top: "90px" },
  cartHeader: { display: "flex", alignItems: "center", gap: "12px", padding: "16px 20px", background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)", color: "white" },
  cartHeaderIcon: { fontSize: "24px", background: "rgba(255, 255, 255, 0.15)", padding: "8px", borderRadius: "10px" },
  cartTitle: { fontSize: "16px", fontWeight: "700", margin: 0 },
  cartSubtitle: { fontSize: "12px", opacity: "0.85", margin: "2px 0 0 0" },
  cartSection: { padding: "16px", maxHeight: "300px", overflowY: "auto" },
  emptyCart: { textAlign: "center", padding: "30px 20px" },
  emptyCartIcon: { fontSize: "48px", marginBottom: "12px", opacity: 0.2 },
  emptyCartText: { fontSize: "16px", fontWeight: "600", color: "#475569", marginBottom: "4px" },
  emptyCartHint: { fontSize: "12px", color: "#94a3b8" },
  cartList: { display: "flex", flexDirection: "column", gap: "10px" },
  cartItem: { padding: "14px", background: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0" },
  cartItemRenta: { background: "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)", border: "1px solid #a5b4fc" },
  rentaBadge: { display: "inline-flex", alignItems: "center", gap: "3px", background: "#4f46e5", color: "#fff", fontSize: "10px", fontWeight: "700", padding: "2px 7px", borderRadius: "6px", marginRight: "6px", letterSpacing: "0.5px", verticalAlign: "middle" },
  cartItemHeader: { display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" },
  cartItemIndex: { width: "26px", height: "26px", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)", color: "white", borderRadius: "8px", fontSize: "11px", fontWeight: "700" },
  cartItemName: { flex: 1, fontSize: "14px", fontWeight: "600", color: "#1e293b" },
  btnDelete: { background: "#f1f5f9", border: "none", borderRadius: "6px", padding: "6px 10px", cursor: "pointer", fontSize: "12px" },
  cartItemControls: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" },
  quantityControl: { display: "flex", gap: "6px", alignItems: "center" },
  btnQty: { width: "32px", height: "32px", border: "2px solid #e2e8f0", borderRadius: "8px", background: "white", cursor: "pointer", fontSize: "16px", fontWeight: "700", color: "#3b82f6" },
  inputQty: { width: "50px", padding: "6px", textAlign: "center", border: "2px solid #e2e8f0", borderRadius: "8px", fontSize: "14px", fontWeight: "700", color: "#1e293b" },
  priceInfo: { textAlign: "right" },
  priceUnit: { display: "block", fontSize: "11px", color: "#64748b", marginBottom: "2px" },
  priceTotal: { display: "block", fontSize: "16px", fontWeight: "800", color: "#1e3a8a" },
  discountControl: { display: "flex", alignItems: "center", gap: "10px", padding: "10px", background: "#fffbeb", borderRadius: "8px", border: "1px solid #fde68a" },
  discountLabel: { display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#92400e", fontWeight: "600" },
  discountIcon: { fontSize: "14px" },
  discountInputBox: { display: "flex", alignItems: "center", gap: "4px" },
  inputDiscount: { width: "50px", padding: "6px", border: "2px solid #fde68a", borderRadius: "6px", fontSize: "13px", fontWeight: "600", textAlign: "center", background: "white" },
  discountPercent: { fontWeight: "700", color: "#92400e" },
  discountSaved: { fontSize: "11px", color: "#059669", fontWeight: "600", marginLeft: "auto" },

  // Totals
  totalsSection: { padding: "16px 20px", background: "linear-gradient(180deg, #f8fafc 0%, #eff6ff 100%)", borderTop: "2px solid #e2e8f0" },
  totalRow: { display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#64748b", marginBottom: "8px" },
  totalValue: { fontWeight: "600", color: "#475569" },
  totalRowFinal: { display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "12px", borderTop: "2px dashed #cbd5e1", marginTop: "8px" },
  totalFinal: { fontSize: "22px", fontWeight: "800", color: "#1e3a8a" },

  // Payment
  paymentSection: { padding: "16px 20px", borderTop: "1px solid #e2e8f0" },
  paymentLabel: { display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: "700", color: "#1e293b", marginBottom: "12px" },
  paymentIcon: { fontSize: "16px" },
  paymentOptions: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "8px", marginBottom: "16px" },
  paymentOption: { padding: "12px", border: "2px solid #e2e8f0", borderRadius: "10px", cursor: "pointer", textAlign: "center", transition: "all 0.2s", display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" },
  paymentOptionActive: { borderColor: "#3b82f6", background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)" },
  paymentOptionIcon: { fontSize: "20px" },
  paymentOptionLabel: { fontSize: "12px", fontWeight: "600", color: "#475569" },
  label: { display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "8px" },
  inputMonto: { width: "100%", padding: "14px", fontSize: "18px", fontWeight: "700", border: "2px solid #e2e8f0", borderRadius: "10px", boxSizing: "border-box", textAlign: "center", outline: "none" },
  efectivoSection: { marginTop: "8px" },
  cambioBox: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px", background: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)", borderRadius: "10px", marginTop: "12px", border: "1px solid #6ee7b7" },
  cambioLabel: { fontSize: "14px", fontWeight: "600", color: "#065f46" },
  cambioMonto: { fontSize: "20px", fontWeight: "800", color: "#047857" },

  // Mixto
  mixtoSection: { marginTop: "8px", display: "flex", flexDirection: "column", gap: "10px" },
  mixtoRow: { display: "flex", alignItems: "center", gap: "10px" },
  mixtoLabel: { fontSize: "13px", fontWeight: "600", color: "#475569", minWidth: "110px" },
  mixtoInput: { flex: 1, padding: "10px", fontSize: "14px", fontWeight: "600", border: "2px solid #e2e8f0", borderRadius: "8px", textAlign: "right", outline: "none" },
  mixtoResumen: { marginTop: "8px", padding: "12px", background: "#f8fafc", borderRadius: "10px", border: "1px solid #e2e8f0" },
  mixtoResumenRow: { display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#475569", marginBottom: "6px" },

  // Button
  btnProcesar: { width: "100%", padding: "18px", background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "white", border: "none", borderRadius: "0 0 20px 20px", fontSize: "16px", fontWeight: "800", cursor: "pointer", boxShadow: "0 4px 16px rgba(16, 185, 129, 0.3)" },
  btnContent: { display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" },
  btnIcon: { fontSize: "18px", fontWeight: "700" },
  btnLoading: { display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" },
  btnSpinner: { width: "20px", height: "20px", border: "3px solid rgba(255,255,255,0.3)", borderTopColor: "white", borderRadius: "50%", animation: "spin 0.8s linear infinite" },

  // Nuevos estilos para RentACar
btnRenta: {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '14px 24px',
  background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
  color: '#1e40af',
  border: '2px solid #93c5fd',
  borderRadius: '12px',
  fontWeight: '700',
  fontSize: '15px',
  cursor: 'pointer',
  transition: 'all 0.2s',
  width: '100%',
},
modalOverlay: {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
},
 modalContent: {
  background: 'white',
  borderRadius: '20px',
  width: '90%',
  maxWidth: '700px',
  maxHeight: '80vh',
  overflow: 'hidden',
  boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
},
modalHeader: {
  padding: '24px',
  borderBottom: '2px solid #e2e8f0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: 'linear-gradient(135deg, #dbeafe 0%, #e0f2fe 100%)',
},
 modalBody: {
  padding: '20px',
  maxHeight: 'calc(80vh - 100px)',
  overflowY: 'auto',
},
 rentaRow: {
  padding: '16px',
  background: '#f8fafc',
  borderRadius: '12px',
  marginBottom: '12px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  border: '2px solid #e2e8f0',
  transition: 'all 0.2s',
  cursor: 'pointer',
},
 btnImportar: {
  marginTop: '8px',
  padding: '8px 16px',
  background: '#10b981',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontWeight: '600',
  cursor: 'pointer',
  fontSize: '13px',
  transition: 'all 0.2s',
},
  btnCloseModal: {
  background: '#fee2e2',
  border: 'none',
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  fontSize: '20px',
  cursor: 'pointer',
  color: '#991b1b',
  fontWeight: 'bold',
  transition: 'all 0.2s',
},
};

export default POS;